import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@/lib/auth-context';
import type {
  SocialPost, SocialChannel, SocialComment, SocialMember, SocialAuthor,
  SocialEvent, SocialAnnotation, ChannelSubscription, NotificationLevel, EventArtifacts,
} from './types';
import type { ConversationSummary } from './ConversationList';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

// ─── helpers ────────────────────────────────────────────────────────

async function api<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${CORE_API}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Request failed ${res.status}`);
  }
  return res.json();
}

function jsonPost(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

// ─── query keys ─────────────────────────────────────────────────────

export const socialKeys = {
  channels: (clubId: string) => ['social', clubId, 'channels'] as const,
  feed: (clubId: string, channel?: string | null) =>
    ['social', clubId, 'feed', channel ?? 'all'] as const,
  conversations: (clubId: string) => ['social', clubId, 'conversations'] as const,
  thread: (clubId: string, convId: string) =>
    ['social', clubId, 'conversations', convId] as const,
  comments: (clubId: string, postId: string) =>
    ['social', clubId, 'posts', postId, 'comments'] as const,
  members: (clubId: string) => ['social', clubId, 'members'] as const,
  events: (clubId: string, filter?: string) =>
    ['social', clubId, 'events', filter ?? 'upcoming'] as const,
  event: (clubId: string, eventId: string) =>
    ['social', clubId, 'events', eventId] as const,
  subscriptions: (clubId: string) => ['social', clubId, 'subscriptions'] as const,
  annotations: (clubId: string, postId: string) =>
    ['social', clubId, 'posts', postId, 'annotations'] as const,
  links: (clubId: string, type?: string) =>
    ['social', clubId, 'links', type ?? 'link'] as const,
  circle: (clubId: string) => ['social', clubId, 'links', 'circle'] as const,
  followers: (clubId: string) => ['social', clubId, 'links', 'followers'] as const,
  linkStatus: (clubId: string, targetId: string) =>
    ['social', clubId, 'links', 'status', targetId] as const,
  hiddenIds: (clubId: string) => ['social', clubId, 'links', 'hidden-ids'] as const,
};

// ─── CHANNELS ───────────────────────────────────────────────────────

export function useChannels(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.channels(clubId),
    queryFn: async () => {
      const json = await api<{ data: { channels: SocialChannel[] } }>(
        `/api/v1/social/${clubId}/channels`,
        token,
      );
      return json.data?.channels || [];
    },
    enabled: !!token,
  });
}

// ─── CHANNEL JOIN / LEAVE ───────────────────────────────────────────

export function useJoinChannel(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/channels/${channelId}/join`,
        token,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.channels(clubId) });
    },
  });
}

export function useLeaveChannel(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/channels/${channelId}/leave`,
        token,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.channels(clubId) });
    },
  });
}

// ─── EDIT POST ──────────────────────────────────────────────────────

export function useEditPost(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/posts/${postId}`,
        token,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

// ─── PIN POST ───────────────────────────────────────────────────────

export function usePinPost(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api<{ data: { pinned: boolean } }>(
        `/api/v1/social/${clubId}/posts/${postId}/pin`,
        token,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

// ─── DELETE POST ────────────────────────────────────────────────────

export function useDeletePost(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api<unknown>(
        `/api/v1/social/${clubId}/posts/${postId}`,
        token,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

// ─── FEED (infinite scroll) ─────────────────────────────────────────

interface FeedPage {
  posts: SocialPost[];
  next_cursor: string | null;
}

export function useClubFeed(clubId: string, channelSlug?: string | null) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useInfiniteQuery({
    queryKey: socialKeys.feed(clubId, channelSlug),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (channelSlug) params.set('channel', channelSlug);
      if (pageParam) params.set('cursor', pageParam);
      params.set('limit', '25');

      const json = await api<{ data: FeedPage }>(
        `/api/v1/social/${clubId}/feed?${params}`,
        token,
      );
      return json.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage?.next_cursor || undefined,
    enabled: !!token,
  });
}

// ─── CONVERSATIONS (list, polled) ───────────────────────────────────

export function useConversations(clubId: string, refetchMs = 15_000) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.conversations(clubId),
    queryFn: async () => {
      const json = await api<{ data: { conversations: ConversationSummary[] } }>(
        `/api/v1/social/${clubId}/conversations`,
        token,
      );
      return json.data?.conversations || [];
    },
    enabled: !!token,
    refetchInterval: refetchMs,
  });
}

// ─── CONVERSATION THREAD (messages, polled) ─────────────────────────

export interface DirectMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  body: string | null;
  media_url: string | null;
  created_at: string;
  sender: SocialAuthor | null;
}

export function useThreadMessages(clubId: string, conversationId: string, refetchMs = 8_000) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.thread(clubId, conversationId),
    queryFn: async () => {
      const json = await api<{ data: { messages: DirectMessage[] } }>(
        `/api/v1/social/${clubId}/conversations/${conversationId}`,
        token,
      );
      return json.data?.messages || [];
    },
    enabled: !!token && !!conversationId,
    refetchInterval: refetchMs,
  });
}

export function useSendMessage(clubId: string, conversationId: string) {
  const { session, user } = useAuth();
  const token = session?.access_token || '';
  const userId = user?.id || '';
  const qc = useQueryClient();
  const threadKey = socialKeys.thread(clubId, conversationId);

  return useMutation({
    mutationFn: async (body: string) => {
      return api<{ data: { message: DirectMessage } }>(
        `/api/v1/social/${clubId}/conversations/${conversationId}/messages`,
        token,
        jsonPost({ body }),
      );
    },
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: threadKey });
      const prev = qc.getQueryData<DirectMessage[]>(threadKey);

      const optimistic: DirectMessage = {
        id: `pending-${Date.now()}`,
        conversation_id: conversationId,
        sender_id: userId,
        body,
        media_url: null,
        created_at: new Date().toISOString(),
        sender: { id: userId, full_name: 'You', avatar_url: null, role: null },
      };

      qc.setQueryData<DirectMessage[]>(threadKey, (old) => [...(old || []), optimistic]);
      return { prev };
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.prev) qc.setQueryData(threadKey, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: threadKey });
      qc.invalidateQueries({ queryKey: socialKeys.conversations(clubId) });
    },
  });
}

export function useMarkRead(clubId: string, conversationId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useMutation({
    mutationFn: () =>
      api<unknown>(
        `/api/v1/social/${clubId}/conversations/${conversationId}/read`,
        token,
        { method: 'POST' },
      ),
  });
}

// ─── COMMENTS ───────────────────────────────────────────────────────

export function useComments(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.comments(clubId, postId),
    queryFn: async () => {
      const json = await api<{ data: { comments: SocialComment[] } }>(
        `/api/v1/social/${clubId}/posts/${postId}/comments`,
        token,
      );
      return json.data?.comments || [];
    },
    enabled: !!token,
  });
}

export function useAddComment(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/posts/${postId}/comments`,
        token,
        jsonPost({ body }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.comments(clubId, postId) });
    },
  });
}

// ─── POSTS ──────────────────────────────────────────────────────────

export interface MediaAttachment {
  url: string;
  type: string;
  alt?: string;
  size?: number;
}

export function useCreatePost(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: { body: string; channel?: string; media_urls?: MediaAttachment[]; quoted_post_id?: string; metadata?: Record<string, unknown> }) =>
      api<unknown>(
        `/api/v1/social/${clubId}/posts`,
        token,
        jsonPost(payload),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

export function useUploadAttachment(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useMutation({
    mutationFn: async (file: File): Promise<MediaAttachment> => {
      // Phase 1: Get signed upload URL
      const res = await api<{
        data: { uploads: { key: string; signedUrl: string; publicUrl: string }[] };
      }>(
        `/api/v1/social/${clubId}/attachments/upload`,
        token,
        jsonPost({ files: [{ name: file.name, size: file.size, content_type: file.type }] }),
      );

      // api() returns the full JSON body; uploads live at res.data.uploads
      const uploads = (res as any).data?.uploads ?? (res as any).uploads;
      const upload = uploads?.[0];
      if (!upload?.signedUrl) throw new Error('Failed to get upload URL');

      // Phase 2: Upload directly to Supabase Storage
      const uploadRes = await fetch(upload.signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadRes.ok) throw new Error('Upload failed');

      return {
        url: upload.publicUrl,
        type: file.type,
        alt: file.name,
        size: file.size,
      };
    },
  });
}

// ─── REACTIONS ──────────────────────────────────────────────────────

export function useReact(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (emoji: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/posts/${postId}/react`,
        token,
        jsonPost({ emoji }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

// ─── SHARE TO COMMUNITY ─────────────────────────────────────────────

export function useSharePost(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api<unknown>(
        `/api/v1/social/${clubId}/posts/${postId}/share`,
        token,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

// ─── MEMBERS ────────────────────────────────────────────────────────

export function useClubMembers(clubId: string) {
  const { session, user } = useAuth();
  const token = session?.access_token || '';
  const currentUserId = user?.id;

  return useQuery({
    queryKey: socialKeys.members(clubId),
    queryFn: async () => {
      const json = await api<{ data: { members: SocialMember[] } }>(
        `/api/v1/social/${clubId}/members?limit=50`,
        token,
      );
      return (json.data?.members || []).filter((m: SocialMember) => m.user?.id !== currentUserId);
    },
    enabled: !!token,
  });
}

// ─── CREATE CONVERSATION ────────────────────────────────────────────

export function useCreateConversation(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: { participant_ids: string[]; message?: string }) =>
      api<{ data: { conversation_id: string } }>(
        `/api/v1/social/${clubId}/conversations`,
        token,
        jsonPost(payload),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.conversations(clubId) });
    },
  });
}

// ─── EVENTS ──────────────────────────────────────────────────────────

export function useClubEvents(clubId: string, filter: 'upcoming' | 'past' | 'all' = 'upcoming') {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.events(clubId, filter),
    queryFn: async () => {
      const json = await api<{ data: { events: SocialEvent[] } }>(
        `/api/v1/social/${clubId}/events?filter=${filter}`,
        token,
      );
      return json.data?.events || [];
    },
    enabled: !!token,
  });
}

export function useCreateEvent(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      title: string;
      description?: string;
      event_type?: string;
      starts_at: string;
      ends_at?: string;
      location?: string;
      max_attendees?: number;
      channel?: string;
      recurrence?: 'weekly' | 'biweekly' | 'monthly';
      credits_entry?: number;
      credits_host_cost?: number;
      requires_approval?: boolean;
    }) =>
      api<{ data: { event: SocialEvent } }>(
        `/api/v1/social/${clubId}/events`,
        token,
        jsonPost(payload),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'events'] });
    },
  });
}

export function useRsvp(clubId: string, eventId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (status: 'going' | 'interested') =>
      api<unknown>(
        `/api/v1/social/${clubId}/events/${eventId}/rsvp`,
        token,
        jsonPost({ status }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'events'] });
    },
  });
}

export function useCancelRsvp(clubId: string, eventId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api<unknown>(
        `/api/v1/social/${clubId}/events/${eventId}/rsvp`,
        token,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'events'] });
    },
  });
}

// ─── SUBSCRIPTIONS ──────────────────────────────────────────────────

export function useSubscriptions(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.subscriptions(clubId),
    queryFn: async () => {
      const json = await api<{ data: { subscriptions: ChannelSubscription[] } }>(
        `/api/v1/social/${clubId}/subscriptions`,
        token,
      );
      return json.data?.subscriptions || [];
    },
    enabled: !!token,
  });
}

export function useUpdateSubscription(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: { channelId: string; notification_level?: NotificationLevel; pinned?: boolean }) =>
      api<unknown>(
        `/api/v1/social/${clubId}/subscriptions/${payload.channelId}`,
        token,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            notification_level: payload.notification_level,
            pinned: payload.pinned,
          }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.subscriptions(clubId) });
    },
  });
}

export function useMarkChannelRead(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (channelId: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/subscriptions/${channelId}/read`,
        token,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.subscriptions(clubId) });
    },
  });
}

// ─── EVENT DETAIL ───────────────────────────────────────────────────

export function useEventDetail(clubId: string, eventId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.event(clubId, eventId),
    queryFn: async () => {
      const json = await api<{ data: { event: SocialEvent } }>(
        `/api/v1/social/${clubId}/events/${eventId}`,
        token,
      );
      return json.data?.event || null;
    },
    enabled: !!token && !!eventId,
  });
}

// ─── EVENT ARTIFACTS ────────────────────────────────────────────────

export function useUpdateArtifacts(clubId: string, eventId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (artifacts: Partial<EventArtifacts>) =>
      api<unknown>(
        `/api/v1/social/${clubId}/events/${eventId}/artifacts`,
        token,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(artifacts),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.event(clubId, eventId) });
      qc.invalidateQueries({ queryKey: ['social', clubId, 'events'] });
    },
  });
}

// ─── EVENT STATUS ───────────────────────────────────────────────────

export function useUpdateEventStatus(clubId: string, eventId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (status: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/events/${eventId}/status`,
        token,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.event(clubId, eventId) });
      qc.invalidateQueries({ queryKey: ['social', clubId, 'events'] });
    },
  });
}

// ─── RECURRING EVENT — SPAWN NEXT ──────────────────────────────────

export function useSpawnNextEvent(clubId: string, eventId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api<{ data: { event: SocialEvent } }>(
        `/api/v1/social/${clubId}/events/${eventId}/spawn-next`,
        token,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'events'] });
      qc.invalidateQueries({ queryKey: socialKeys.event(clubId, eventId) });
    },
  });
}

// ─── ANNOTATIONS ───────────────────────────────────────────────────

export function useAnnotations(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.annotations(clubId, postId),
    queryFn: async () => {
      const json = await api<{ data: { annotations: SocialAnnotation[] } }>(
        `/api/v1/social/${clubId}/posts/${postId}/annotations`,
        token,
      );
      return json.data?.annotations || [];
    },
    enabled: !!token && !!postId,
  });
}

export function useCreateAnnotation(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      start_offset: number;
      end_offset: number;
      highlighted_text: string;
      annotation_type: string;
      body?: string;
    }) =>
      api<unknown>(
        `/api/v1/social/${clubId}/posts/${postId}/annotations`,
        token,
        jsonPost(payload),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: socialKeys.annotations(clubId, postId) });
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

export function useDeleteAnnotation(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (annotationId: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/annotations/${annotationId}`,
        token,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId] });
    },
  });
}

export function useSpawnAnnotationDiscussion(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: { annotationId: string; body?: string }) =>
      api<{ data: { post_id: string } }>(
        `/api/v1/social/${clubId}/annotations/${payload.annotationId}/discuss`,
        token,
        jsonPost({ body: payload.body }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

export function useResolvePost(clubId: string, postId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api<unknown>(
        `/api/v1/social/${clubId}/posts/${postId}/resolve`,
        token,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

// ─── FACT CHECK ────────────────────────────────────────────────────

export function useFactCheck(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      post_id: string;
      highlighted_text: string;
      start_offset: number;
      end_offset: number;
    }) =>
      api<{ data: { annotations: SocialAnnotation[] } }>(
        `/api/v1/social/${clubId}/fact-check`,
        token,
        jsonPost(payload),
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: socialKeys.annotations(clubId, variables.post_id) });
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

// ─── HIGHLIGHTS FEED ────────────────────────────────────────────────

export function useHighlights(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: ['social', clubId, 'highlights'],
    queryFn: async () => {
      const json = await api<{ data: { posts: SocialPost[]; next_cursor: string | null } }>(
        `/api/v1/social/${clubId}/highlights`,
        token,
      );
      return json.data?.posts || [];
    },
    enabled: !!token,
  });
}

/** Top highlighted post — powers NumaPick (Bento top-left) */
export function useNumaPick(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: ['social', clubId, 'numa-pick'],
    queryFn: async () => {
      const json = await api<{ data: { posts: SocialPost[]; next_cursor: string | null } }>(
        `/api/v1/social/${clubId}/highlights?limit=1`,
        token,
      );
      return (json.data?.posts || [])[0] || null;
    },
    enabled: !!token && !!clubId,
    staleTime: 5 * 60_000, // cache 5 min — it's the "pick of the day"
  });
}

/** System announcements for TowerMarquee */
export function useSystemAnnouncements(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: ['social', clubId, 'announcements'],
    queryFn: async () => {
      const json = await api<{ feed: SocialPost[] }>(
        `/api/v1/social/${clubId}/feed?limit=10`,
        token,
      );
      // Filter to system posts and announcements from last 24h
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      return (json.feed || []).filter(
        (p) =>
          (p.author_type === 'system' || p.post_type === 'announcement') &&
          new Date(p.created_at).getTime() > cutoff,
      );
    },
    enabled: !!token && !!clubId,
    staleTime: 5 * 60_000,
  });
}

// ─── MY ACTIVITY ───────────────────────────────────────────────────

export type ActivityRange = '24h' | '48h' | '7d' | '30d' | 'all';

export interface MyActivityPost {
  id: string;
  body: string | null;
  post_type: string;
  media_urls: { url: string; type: string; alt?: string }[];
  is_pinned: boolean;
  shared_to_community: boolean;
  is_deleted: boolean;
  created_at: string;
  channel: { id: string; name: string; slug: string } | null;
  comment_count: number;
  reaction_count: number;
}

export interface MyActivityComment {
  id: string;
  body: string;
  post_id: string;
  is_deleted: boolean;
  created_at: string;
  parent_post_snippet: string;
  parent_post_author: string;
}

export function useMyActivity(clubId: string, range: ActivityRange = '7d') {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: ['social', clubId, 'my-activity', range],
    queryFn: async () => {
      const json = await api<{
        data: { posts: MyActivityPost[]; comments: MyActivityComment[]; range: string };
      }>(
        `/api/v1/social/${clubId}/my-activity?range=${range}`,
        token,
      );
      return json.data;
    },
    enabled: !!token,
  });
}

export function useDeleteMyPost(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (postId: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/my-activity/posts/${postId}`,
        token,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'my-activity'] });
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

export function useDeleteMyComment(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) =>
      api<unknown>(
        `/api/v1/social/${clubId}/my-activity/comments/${commentId}`,
        token,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social', clubId, 'my-activity'] });
      qc.invalidateQueries({ queryKey: ['social', clubId, 'feed'] });
    },
  });
}

// ─── LINKS ──────────────────────────────────────────────────────────

export interface SocialLink {
  id: string;
  link_type: 'link' | 'block' | 'hide';
  created_at: string;
  updated_at: string;
  target: SocialAuthor | null;
  mutual?: boolean;
}

export interface LinkStatus {
  my_link: 'link' | 'block' | 'hide' | null;
  their_link: 'link' | 'block' | 'hide' | null;
  mutual: boolean;
}

/** My links (people I follow) in this club */
export function useMyLinks(clubId: string, type: string = 'link') {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.links(clubId, type),
    queryFn: () =>
      api<{ data: SocialLink[] }>(
        `/api/v1/social/${clubId}/links?type=${type}`,
        token,
      ).then((r) => r.data),
    enabled: !!token && !!clubId,
  });
}

/** "Your Circle" feed — posts from people I've linked with */
export function useCircleFeed(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useInfiniteQuery({
    queryKey: socialKeys.circle(clubId),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '10' });
      if (pageParam) params.set('cursor', pageParam);
      return api<{ feed: SocialPost[]; nextCursor: string | null }>(
        `/api/v1/social/${clubId}/links/circle?${params}`,
        token,
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!token && !!clubId,
  });
}

/** People who follow me in this club */
export function useMyFollowers(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.followers(clubId),
    queryFn: () =>
      api<{ data: Array<{ id: string; created_at: string; actor: SocialAuthor | null; mutual: boolean }> }>(
        `/api/v1/social/${clubId}/links/followers`,
        token,
      ).then((r) => r.data),
    enabled: !!token && !!clubId,
  });
}

/** Link status with a specific user (am I linked, are they linked back) */
export function useLinkStatus(clubId: string, targetId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.linkStatus(clubId, targetId),
    queryFn: () =>
      api<{ data: LinkStatus }>(
        `/api/v1/social/${clubId}/links/status/${targetId}`,
        token,
      ).then((r) => r.data),
    enabled: !!token && !!clubId && !!targetId,
  });
}

/** IDs of users I've hidden or blocked (for client-side feed filtering) */
export function useHiddenUserIds(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';

  return useQuery({
    queryKey: socialKeys.hiddenIds(clubId),
    queryFn: () =>
      api<{ data: string[] }>(
        `/api/v1/social/${clubId}/links/hidden-ids`,
        token,
      ).then((r) => r.data),
    enabled: !!token && !!clubId,
    staleTime: 60_000, // cache for 1 minute
  });
}

/** Create a link (follow), block, or hide */
export function useCreateLink(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ target_id, link_type }: { target_id: string; link_type: 'link' | 'block' | 'hide' }) =>
      api<{ data: { id: string; link_type: string; target_id: string; mutual: boolean } }>(
        `/api/v1/social/${clubId}/links`,
        token,
        jsonPost({ target_id, link_type }),
      ),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: socialKeys.links(clubId) });
      qc.invalidateQueries({ queryKey: socialKeys.circle(clubId) });
      qc.invalidateQueries({ queryKey: socialKeys.hiddenIds(clubId) });
      qc.invalidateQueries({ queryKey: socialKeys.linkStatus(clubId, variables.target_id) });
      // Refresh feed if hiding/blocking someone
      if (variables.link_type === 'hide' || variables.link_type === 'block') {
        qc.invalidateQueries({ queryKey: socialKeys.feed(clubId) });
      }
    },
  });
}

/** Unlink (remove a follow) */
export function useUnlink(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (targetId: string) =>
      api<{ data: { unlinked: boolean } }>(
        `/api/v1/social/${clubId}/links/${targetId}`,
        token,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, targetId) => {
      qc.invalidateQueries({ queryKey: socialKeys.links(clubId) });
      qc.invalidateQueries({ queryKey: socialKeys.circle(clubId) });
      qc.invalidateQueries({ queryKey: socialKeys.linkStatus(clubId, targetId) });
    },
  });
}

/** Unhide a user */
export function useUnhide(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (targetId: string) =>
      api<{ data: { unhidden: boolean } }>(
        `/api/v1/social/${clubId}/links/${targetId}/hide`,
        token,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, targetId) => {
      qc.invalidateQueries({ queryKey: socialKeys.hiddenIds(clubId) });
      qc.invalidateQueries({ queryKey: socialKeys.feed(clubId) });
      qc.invalidateQueries({ queryKey: socialKeys.linkStatus(clubId, targetId) });
    },
  });
}

/** Unblock a user */
export function useUnblock(clubId: string) {
  const { session } = useAuth();
  const token = session?.access_token || '';
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (targetId: string) =>
      api<{ data: { unblocked: boolean } }>(
        `/api/v1/social/${clubId}/links/${targetId}/block`,
        token,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, targetId) => {
      qc.invalidateQueries({ queryKey: socialKeys.hiddenIds(clubId) });
      qc.invalidateQueries({ queryKey: socialKeys.linkStatus(clubId, targetId) });
    },
  });
}
